import { useState, useEffect } from 'react';
import { PageHeader, Card, LoadingSpinner } from '../components/Layout';
import api from '../services/api';

interface TrainingModule {
  id: string;
  name: string;
  description: string;
  videoUrl: string | null;
  category: string;
  pointsAwarded: number;
  sortOrder: number;
  active: boolean;
  durationMinutes: number | null;
  questionCount: number;
}

interface Question {
  id: string;
  moduleId: string;
  questionText: string;
  optionA: string;
  optionB: string;
  optionC: string;
  optionD: string;
  correctAnswer: string;
  sortOrder: number;
  active: boolean;
}

export default function DealerTrainingManagementPage() {
  const [loading, setLoading] = useState(true);
  const [modules, setModules] = useState<TrainingModule[]>([]);
  const [expandedModule, setExpandedModule] = useState<string | null>(null);
  const [questions, setQuestions] = useState<Record<string, Question[]>>({});
  const [loadingQuestions, setLoadingQuestions] = useState<string | null>(null);

  // Module form
  const [showModuleForm, setShowModuleForm] = useState(false);
  const [editingModule, setEditingModule] = useState<TrainingModule | null>(null);
  const [moduleForm, setModuleForm] = useState({
    name: '', description: '', videoUrl: '', category: 'installation',
    pointsAwarded: '100', sortOrder: '0', durationMinutes: '',
  });

  // Question form
  const [showQuestionForm, setShowQuestionForm] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState<Question | null>(null);
  const [questionModuleId, setQuestionModuleId] = useState<string | null>(null);
  const [questionForm, setQuestionForm] = useState({
    questionText: '', optionA: '', optionB: '', optionC: '', optionD: '',
    correctAnswer: 'a', sortOrder: '0',
  });

  const [saving, setSaving] = useState(false);

  useEffect(() => { loadModules(); }, []);

  const loadModules = async () => {
    try {
      setLoading(true);
      const data = await api.getDealerTrainingModules();
      setModules(data || []);
    } catch (err: any) {
      console.error('Failed to load modules:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadQuestions = async (moduleId: string) => {
    try {
      setLoadingQuestions(moduleId);
      const data = await api.getModuleQuestions(moduleId);
      setQuestions(prev => ({ ...prev, [moduleId]: data || [] }));
    } catch (err: any) {
      console.error('Failed to load questions:', err);
    } finally {
      setLoadingQuestions(null);
    }
  };

  const toggleModule = (moduleId: string) => {
    if (expandedModule === moduleId) {
      setExpandedModule(null);
    } else {
      setExpandedModule(moduleId);
      if (!questions[moduleId]) loadQuestions(moduleId);
    }
  };

  // Module CRUD
  const openCreateModule = () => {
    setEditingModule(null);
    setModuleForm({ name: '', description: '', videoUrl: '', category: 'installation', pointsAwarded: '100', sortOrder: '0', durationMinutes: '' });
    setShowModuleForm(true);
  };

  const openEditModule = (m: TrainingModule) => {
    setEditingModule(m);
    setModuleForm({
      name: m.name, description: m.description || '', videoUrl: m.videoUrl || '',
      category: m.category || 'installation', pointsAwarded: String(m.pointsAwarded),
      sortOrder: String(m.sortOrder || 0), durationMinutes: m.durationMinutes ? String(m.durationMinutes) : '',
    });
    setShowModuleForm(true);
  };

  const saveModule = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!moduleForm.name.trim()) return alert('Name is required');
    setSaving(true);
    try {
      const payload = {
        name: moduleForm.name,
        description: moduleForm.description || null,
        videoUrl: moduleForm.videoUrl || null,
        category: moduleForm.category,
        pointsAwarded: parseInt(moduleForm.pointsAwarded) || 100,
        sortOrder: parseInt(moduleForm.sortOrder) || 0,
        durationMinutes: moduleForm.durationMinutes ? parseInt(moduleForm.durationMinutes) : null,
      };
      if (editingModule) {
        await api.updateDealerTrainingModule(editingModule.id, payload);
      } else {
        await api.createDealerTrainingModule(payload);
      }
      setShowModuleForm(false);
      loadModules();
    } catch (err: any) {
      alert(err.message || 'Failed to save module');
    } finally {
      setSaving(false);
    }
  };

  const toggleModuleActive = async (m: TrainingModule) => {
    try {
      await api.updateDealerTrainingModule(m.id, { active: !m.active });
      loadModules();
    } catch (err: any) {
      alert(err.message || 'Failed to update');
    }
  };

  // Question CRUD
  const openCreateQuestion = (moduleId: string) => {
    setEditingQuestion(null);
    setQuestionModuleId(moduleId);
    setQuestionForm({ questionText: '', optionA: '', optionB: '', optionC: '', optionD: '', correctAnswer: 'a', sortOrder: '0' });
    setShowQuestionForm(true);
  };

  const openEditQuestion = (q: Question) => {
    setEditingQuestion(q);
    setQuestionModuleId(q.moduleId);
    setQuestionForm({
      questionText: q.questionText, optionA: q.optionA, optionB: q.optionB,
      optionC: q.optionC, optionD: q.optionD, correctAnswer: q.correctAnswer,
      sortOrder: String(q.sortOrder || 0),
    });
    setShowQuestionForm(true);
  };

  const saveQuestion = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!questionForm.questionText.trim() || !questionForm.optionA.trim() ||
        !questionForm.optionB.trim() || !questionForm.optionC.trim() || !questionForm.optionD.trim()) {
      return alert('All fields are required');
    }
    setSaving(true);
    try {
      const payload = {
        questionText: questionForm.questionText,
        optionA: questionForm.optionA,
        optionB: questionForm.optionB,
        optionC: questionForm.optionC,
        optionD: questionForm.optionD,
        correctAnswer: questionForm.correctAnswer,
        sortOrder: parseInt(questionForm.sortOrder) || 0,
      };
      if (editingQuestion) {
        await api.updateModuleQuestion(editingQuestion.id, payload);
      } else {
        await api.createModuleQuestion(questionModuleId!, payload);
      }
      setShowQuestionForm(false);
      if (questionModuleId) loadQuestions(questionModuleId);
      loadModules();
    } catch (err: any) {
      alert(err.message || 'Failed to save question');
    } finally {
      setSaving(false);
    }
  };

  const deleteQuestion = async (q: Question) => {
    if (!confirm('Delete this question?')) return;
    try {
      await api.deleteModuleQuestion(q.id);
      if (q.moduleId) loadQuestions(q.moduleId);
      loadModules();
    } catch (err: any) {
      alert(err.message || 'Failed to delete');
    }
  };

  if (loading) return <div className="flex justify-center items-center min-h-96"><LoadingSpinner size="lg" /></div>;

  const CATEGORIES = ['installation', 'sales', 'product', 'operations'];

  return (
    <div>
      <PageHeader
        title="Dealer Training Modules"
        subtitle="Manage training videos, quiz questions, and point awards for dealer training"
        actions={
          <button onClick={openCreateModule} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
            + New Module
          </button>
        }
      />

      <Card>
        {modules.length === 0 ? (
          <p className="text-gray-500 text-center py-8">No training modules yet</p>
        ) : (
          <div className="divide-y divide-gray-200">
            {modules.map(m => (
              <div key={m.id}>
                {/* Module row */}
                <div className="flex items-center justify-between p-4 hover:bg-gray-50 cursor-pointer" onClick={() => toggleModule(m.id)}>
                  <div className="flex items-center gap-4 flex-1 min-w-0">
                    <button className="text-gray-400 hover:text-gray-600 text-lg flex-shrink-0">
                      {expandedModule === m.id ? '\u25BC' : '\u25B6'}
                    </button>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900 truncate">{m.name}</span>
                        {!m.active && <span className="text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded">Inactive</span>}
                        {!m.videoUrl && <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded">No Video</span>}
                      </div>
                      <div className="text-sm text-gray-500 flex gap-3 mt-0.5">
                        <span className="capitalize">{m.category}</span>
                        <span>{m.pointsAwarded} pts</span>
                        <span>{m.questionCount} question{m.questionCount !== 1 ? 's' : ''}</span>
                        {m.durationMinutes && <span>{m.durationMinutes} min</span>}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0" onClick={e => e.stopPropagation()}>
                    <button onClick={() => toggleModuleActive(m)} className={`text-xs px-3 py-1 rounded-full border transition-colors ${m.active ? 'border-green-300 text-green-700 hover:bg-green-50' : 'border-gray-300 text-gray-500 hover:bg-gray-50'}`}>
                      {m.active ? 'Active' : 'Inactive'}
                    </button>
                    <button onClick={() => openEditModule(m)} className="text-blue-600 hover:text-blue-700 text-sm font-medium px-2 py-1">
                      Edit
                    </button>
                  </div>
                </div>

                {/* Expanded questions panel */}
                {expandedModule === m.id && (
                  <div className="bg-gray-50 border-t border-gray-200 px-4 py-4 pl-12">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-sm font-semibold text-gray-700">Quiz Questions</h4>
                      <button onClick={() => openCreateQuestion(m.id)} className="text-sm px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700">
                        + Add Question
                      </button>
                    </div>

                    {loadingQuestions === m.id ? (
                      <div className="flex justify-center py-4"><LoadingSpinner size="sm" /></div>
                    ) : (questions[m.id] || []).length === 0 ? (
                      <p className="text-gray-400 text-sm py-4 text-center">No questions yet. Add some to enable the quiz.</p>
                    ) : (
                      <div className="space-y-2">
                        {(questions[m.id] || []).map((q, i) => (
                          <div key={q.id} className="bg-white border border-gray-200 rounded-lg p-3">
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex-1 min-w-0">
                                <div className="text-sm font-medium text-gray-900 mb-1">
                                  {i + 1}. {q.questionText}
                                </div>
                                <div className="grid grid-cols-2 gap-1 text-xs text-gray-600">
                                  <div className={q.correctAnswer === 'a' ? 'text-green-700 font-semibold' : ''}>A: {q.optionA}</div>
                                  <div className={q.correctAnswer === 'b' ? 'text-green-700 font-semibold' : ''}>B: {q.optionB}</div>
                                  <div className={q.correctAnswer === 'c' ? 'text-green-700 font-semibold' : ''}>C: {q.optionC}</div>
                                  <div className={q.correctAnswer === 'd' ? 'text-green-700 font-semibold' : ''}>D: {q.optionD}</div>
                                </div>
                              </div>
                              <div className="flex gap-1 flex-shrink-0">
                                <button onClick={() => openEditQuestion(q)} className="text-blue-600 hover:text-blue-700 text-xs px-2 py-1">Edit</button>
                                <button onClick={() => deleteQuestion(q)} className="text-red-600 hover:text-red-700 text-xs px-2 py-1">Del</button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Module Form Modal */}
      {showModuleForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <Card className="max-w-xl w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              {editingModule ? 'Edit Module' : 'Create Module'}
            </h2>
            <form onSubmit={saveModule} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                <input type="text" value={moduleForm.name} onChange={e => setModuleForm({ ...moduleForm, name: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" required />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea value={moduleForm.description} onChange={e => setModuleForm({ ...moduleForm, description: e.target.value })} rows={2} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Video Embed URL</label>
                <input type="url" value={moduleForm.videoUrl} onChange={e => setModuleForm({ ...moduleForm, videoUrl: e.target.value })} placeholder="https://www.youtube.com/embed/..." className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" />
                <p className="text-xs text-gray-500 mt-1">YouTube or Vimeo embed URL. Leave blank to keep module as Coming Soon.</p>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                  <select value={moduleForm.category} onChange={e => setModuleForm({ ...moduleForm, category: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500">
                    {CATEGORIES.map(c => <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Points</label>
                  <input type="number" value={moduleForm.pointsAwarded} onChange={e => setModuleForm({ ...moduleForm, pointsAwarded: e.target.value })} onWheel={e => e.currentTarget.blur()} min="0" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Duration (min)</label>
                  <input type="number" value={moduleForm.durationMinutes} onChange={e => setModuleForm({ ...moduleForm, durationMinutes: e.target.value })} onWheel={e => e.currentTarget.blur()} min="0" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Sort Order</label>
                <input type="number" value={moduleForm.sortOrder} onChange={e => setModuleForm({ ...moduleForm, sortOrder: e.target.value })} onWheel={e => e.currentTarget.blur()} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" />
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t">
                <button type="button" onClick={() => setShowModuleForm(false)} disabled={saving} className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50">Cancel</button>
                <button type="submit" disabled={saving} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">{saving ? 'Saving...' : editingModule ? 'Update' : 'Create'}</button>
              </div>
            </form>
          </Card>
        </div>
      )}

      {/* Question Form Modal */}
      {showQuestionForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <Card className="max-w-xl w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              {editingQuestion ? 'Edit Question' : 'Add Question'}
            </h2>
            <form onSubmit={saveQuestion} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Question *</label>
                <textarea value={questionForm.questionText} onChange={e => setQuestionForm({ ...questionForm, questionText: e.target.value })} rows={2} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" required />
              </div>
              <div className="space-y-3">
                {(['A', 'B', 'C', 'D'] as const).map(letter => {
                  const key = `option${letter}` as 'optionA' | 'optionB' | 'optionC' | 'optionD';
                  return (
                    <div key={letter} className="flex items-center gap-2">
                      <input
                        type="radio"
                        name="correctAnswer"
                        checked={questionForm.correctAnswer === letter.toLowerCase()}
                        onChange={() => setQuestionForm({ ...questionForm, correctAnswer: letter.toLowerCase() })}
                        className="h-4 w-4 text-green-600 border-gray-300 focus:ring-green-500"
                      />
                      <label className="text-sm font-medium text-gray-700 w-4">{letter}.</label>
                      <input
                        type="text"
                        value={questionForm[key]}
                        onChange={e => setQuestionForm({ ...questionForm, [key]: e.target.value })}
                        placeholder={`Option ${letter}`}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        required
                      />
                    </div>
                  );
                })}
                <p className="text-xs text-gray-500">Select the radio button next to the correct answer.</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Sort Order</label>
                <input type="number" value={questionForm.sortOrder} onChange={e => setQuestionForm({ ...questionForm, sortOrder: e.target.value })} onWheel={e => e.currentTarget.blur()} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500" />
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t">
                <button type="button" onClick={() => setShowQuestionForm(false)} disabled={saving} className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50">Cancel</button>
                <button type="submit" disabled={saving} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">{saving ? 'Saving...' : editingQuestion ? 'Update' : 'Add Question'}</button>
              </div>
            </form>
          </Card>
        </div>
      )}
    </div>
  );
}
